/**
 * Campos cadastrais do usuário: nome, e-mail, telefone, papel, cliente e situação
 * de acesso. Extraído do antigo `UserFields` para conviver com as abas.
 */
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { Send } from 'lucide-react'
import { RHFMultiAutocomplete, RHFSelect, RHFSwitch, RHFText } from '../../shared/form/fields'
import { useToast } from '../../shared/feedback/toast'
import { apiErrorMessage } from '../../shared/apiError'
import { http } from '../../core/api/client'
import { CATALOG_STALE_TIME } from '../../core/query/queryClient'
import { customersResource } from '../cadastros/resources'
import { customerOptionsLoader, empresaPaiDoCliente } from '../../shared/form/catalogLoaders'
import { useI18n } from '@/core/i18n/useI18n'
import { CUSTOMER_ROLES, type Variante } from './usuarioForm'
import type { Option } from '../../shared/form/fields'

const PAPEIS = ['staff_admin', 'staff_operator', 'customer_admin', 'customer_operator'] as const

function CampoCliente() {
  const { t } = useI18n()
  const role = useWatch({ name: 'role' }) as string
  const clientes = customersResource.useList({ per_page: 100 }, { staleTime: CATALOG_STALE_TIME })
  if (!CUSTOMER_ROLES.includes(role as never)) return null
  const options: Option[] = (clientes.data?.items ?? []).map((c) => ({
    value: c.uuid,
    label: c.trade_name || c.legal_name,
    sublabel: empresaPaiDoCliente(c),
  }))
  // Multisseleção desde 21/09/2026: o mesmo grupo aparece no cadastro como várias
  // unidades, uma por praça, e quem acompanha o grupo precisava de um login por
  // unidade. A PRIMEIRA escolhida é a principal, e é dela o que a pessoa criar
  // quando o próprio registro não disser o dono — por isso o texto de apoio.
  return (
    <RHFMultiAutocomplete
      name="tenant_uuids"
      label={t('usuarios.form.field.customers')}
      helperText={t('usuarios.form.help.customers')}
      loadOptions={customerOptionsLoader}
      selectedOptions={options}
      asyncKey="users:customers"
      placeholder={t('usuarios.form.search.customers')}
    />
  )
}

/** Reenvio da senha temporária.
 *
 *  O envio do e-mail é deliberadamente tolerante a falha no backend, para não
 *  derrubar a criação do usuário. A contrapartida é esta: sem um reenvio ao
 *  alcance de quem administra, a conta cujo e-mail não chegou ficava travada. */
function ReenviarAcesso() {
  const { t } = useI18n()
  const toast = useToast()
  const { uuid } = useParams()
  const [ocupado, setOcupado] = useState(false)

  if (!uuid) return null

  async function reenviar() {
    setOcupado(true)
    try {
      await http.post(`/users/${uuid}/reenviar-acesso`)
      toast.success(t('usuarios.form.access.sent'))
    } catch (erro) {
      toast.error(apiErrorMessage(erro))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
        {t('usuarios.form.access.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        {t('usuarios.form.access.description')}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Send size={16} />}
        disabled={ocupado}
        onClick={reenviar}
      >
        {ocupado ? t('usuarios.form.access.sending') : t('usuarios.form.access.resend')}
      </Button>
    </Box>
  )
}

export function IdentidadeSection({
  editando,
  variante,
}: {
  editando: boolean
  variante: Variante
}) {
  const { t } = useI18n()
  const opcoesDePapel: Option[] = PAPEIS.map((value) => ({
    value,
    label: t(`usuarios.form.role.${value}`),
  }))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <RHFText name="name" label={t('usuarios.form.field.name')} required />
        <RHFText
          name="email"
          label={t('usuarios.form.field.email')}
          type="email"
          required
          disabled={editando}
          helperText={editando ? t('usuarios.form.emailLocked') : t('usuarios.form.emailHint')}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {/* O portal não escolhe papel: todo sub-usuário nasce operador do cliente,
            e o backend força isso independentemente do que chegue no corpo. */}
        {variante === 'backoffice' && (
          <RHFSelect name="role" label={t('usuarios.form.field.role')} options={opcoesDePapel} />
        )}
        <RHFText name="phone" label={t('usuarios.form.field.phone')} />
      </Box>
      {variante === 'backoffice' && <CampoCliente />}
      {editando && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <RHFSwitch name="is_active" label={t('usuarios.form.field.active')} />
          <RHFSwitch name="is_blocked" label={t('usuarios.form.field.blocked')} />
        </Box>
      )}
      {editando && <ReenviarAcesso />}
    </Box>
  )
}
